// The cover over the board while a match is set up, see `state/boardCover.ts`.
// One cell for each square. A cell steps through a quarter dither, a half, and
// solid, as a puff of a cloud does in the other order. The cells close in rings
// from the edge of the board, and open from the middle. Each cell has a small
// random delay, so the rings are not hard lines. The delays are from
// `Math.random`, because the cover is presentation.
// The cover lies on the box of the board, so its cells are the squares. The
// stage is under it, and the coin flip of a room is over it.

import {
  BOARD_COVER_JITTER_MS,
  BOARD_COVER_RING_MS,
  BOARD_COVER_RINGS,
  BOARD_COVER_STEP_MS,
  BOARD_SIZE,
} from '../constants';
import { el } from '../lib/dom';
import { type BoardCover, boardCoverStore } from '../state/boardCover';

/** 0 for the four squares in the middle, up to `BOARD_COVER_RINGS - 1` at the edge. */
const ringOf = (column: number, row: number): number => {
  const middle = (BOARD_SIZE - 1) / 2;
  return Math.floor(Math.max(Math.abs(column - middle), Math.abs(row - middle)));
};

const delayFor = (ring: number, cover: BoardCover): number => {
  const order = cover === 'closing' ? BOARD_COVER_RINGS - 1 - ring : ring;
  return order * BOARD_COVER_RING_MS + Math.random() * BOARD_COVER_JITTER_MS;
};

/** `board` is the element of the board. The cover takes its box. */
export const createBoardCover = (board: Element): HTMLElement => {
  const cells = Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, index) => {
    const ring = ringOf(index % BOARD_SIZE, Math.floor(index / BOARD_SIZE));
    return { ring, node: el('span', { className: 'board-cover__cell' }) };
  });
  const root = el(
    'div',
    { className: 'board-cover', ariaHidden: 'true' },
    cells.map((cell) => cell.node),
  );
  root.style.setProperty('--cover-step', `${BOARD_COVER_STEP_MS}ms`);

  const place = (): void => {
    const frame = root.parentElement;
    if (!frame) return;
    const box = board.getBoundingClientRect();
    const outer = frame.getBoundingClientRect();
    root.style.left = `${box.left - outer.left - frame.clientLeft}px`;
    root.style.top = `${box.top - outer.top - frame.clientTop}px`;
    root.style.width = `${box.width}px`;
    root.style.height = `${box.height}px`;
  };
  new ResizeObserver(place).observe(board);

  const draw = (cover: BoardCover): void => {
    place();
    if (cover === 'closing' || cover === 'opening') {
      for (const cell of cells) {
        cell.node.style.animationDelay = `${Math.round(delayFor(cell.ring, cover))}ms`;
      }
    }
    root.dataset.cover = cover;
  };
  draw(boardCoverStore.get());
  boardCoverStore.subscribe(draw);
  return root;
};
